// @ts-nocheck
import { forwardRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import { ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery } from '@mui/material';

// project import
import useMenu from 'contexts/MenuContext';

// types
import { NavItemType } from 'types/menu';
import { ThemeMode } from 'types/config';

// ==============================|| NAVIGATION - LIST ITEM ||============================== //

// front-end's NavItem (vertical branch), with the selected item taken from the URL instead
// of the redux `openItem` list, and none of the agent-specific click handling.
interface Props {
  item: NavItemType;
  level: number;
}

const NavItem = ({ item, level }: Props) => {
  const theme = useTheme();
  const { drawerOpen, openDrawer } = useMenu();
  const matchDownLg = useMediaQuery(theme.breakpoints.down('lg'));
  const { pathname } = useLocation();

  const listItemProps = {
    component: forwardRef((props, ref: any) => <Link {...props} to={item.url!} ref={ref} />)
  };

  const Icon = item.icon!;
  const itemIcon = item.icon ? <Icon style={{ fontSize: drawerOpen ? '1rem' : '1.25rem' }} /> : false;

  const isSelected = !!item.url && (pathname === item.url || pathname.startsWith(`${item.url}/`));

  const textColor = theme.palette.mode === ThemeMode.DARK ? 'grey.400' : 'text.primary';
  const iconSelectedColor = theme.palette.mode === ThemeMode.DARK && drawerOpen ? 'text.primary' : 'var(--nv-text-primary)';
  const itemBaseStyles = {
    mx: 0,
    mb: drawerOpen ? 0.375 : 0.75,
    minHeight: drawerOpen ? 40 : 44,
    borderRadius: 2.5,
    px: drawerOpen ? 1 : 0.5,
    transition: theme.transitions.create(['background-color', 'color', 'padding'], {
      duration: theme.transitions.duration.shorter
    })
  };
  const selectedStyles = {
    bgcolor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    boxShadow: `0 12px 24px ${theme.palette.primary.main}22`,
    '& .MuiListItemIcon-root, & .MuiTypography-root, & .MuiChip-label': {
      color: theme.palette.primary.contrastText
    },
    '&:hover': {
      bgcolor: theme.palette.primary.dark
    }
  };

  return (
    <ListItemButton
      {...listItemProps}
      disabled={item.disabled}
      selected={isSelected}
      sx={{
        zIndex: 1201,
        ...itemBaseStyles,
        justifyContent: drawerOpen ? 'flex-start' : 'center',
        pl: drawerOpen ? `${8 + level * 12}px` : 1,
        pr: 1,
        py: !drawerOpen && level === 1 ? 0.5 : 0.625,
        ...(drawerOpen && {
          color: textColor,
          '&:hover': {
            bgcolor: theme.palette.mode === ThemeMode.DARK ? 'divider' : 'grey.100'
          },
          '&.Mui-selected': {
            ...selectedStyles,
            '&:hover': {
              ...selectedStyles['&:hover']
            }
          }
        }),
        ...(!drawerOpen && {
          bgcolor: 'transparent',
          '&:hover': {
            bgcolor: 'transparent'
          },
          '&.Mui-selected': {
            bgcolor: 'transparent',
            '&:hover': {
              bgcolor: 'transparent'
            }
          }
        })
      }}
      onClick={() => {
        if (matchDownLg) openDrawer(false);
      }}
    >
      {itemIcon && (
        <ListItemIcon
          sx={{
            minWidth: drawerOpen ? 24 : 40,
            mr: drawerOpen ? 0.625 : 0,
            color: isSelected && drawerOpen ? theme.palette.primary.contrastText : isSelected ? iconSelectedColor : textColor,
            ...(!drawerOpen && {
              borderRadius: 2,
              width: 40,
              height: 40,
              border: '1px solid',
              borderColor: isSelected
                ? theme.palette.mode === ThemeMode.DARK
                  ? 'var(--nv-brand-on-soft)'
                  : theme.palette.primary.light
                : theme.palette.mode === ThemeMode.DARK
                ? theme.palette.divider
                : theme.palette.grey[200],
              bgcolor: isSelected
                ? theme.palette.mode === ThemeMode.DARK
                  ? 'primary.900'
                  : 'primary.lighter'
                : theme.palette.mode === ThemeMode.DARK
                ? 'background.paper'
                : '#fff',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: theme.palette.mode === ThemeMode.DARK ? 'none' : '0 2px 10px rgba(15, 23, 42, 0.04)',
              '&:hover': {
                bgcolor: theme.palette.mode === ThemeMode.DARK ? 'secondary.light' : 'grey.50'
              }
            })
          }}
        >
          {itemIcon}
        </ListItemIcon>
      )}
      {(drawerOpen || (!drawerOpen && level !== 1)) && (
        <ListItemText
          primary={
            <Typography
              variant="h6"
              sx={{
                color: drawerOpen && isSelected ? theme.palette.primary.contrastText : isSelected ? iconSelectedColor : textColor,
                fontSize: '0.875rem',
                lineHeight: 1.25,
                fontWeight: 500,
                letterSpacing: 0
              }}
            >
              {item.title}
            </Typography>
          }
          sx={{
            my: 0,
            flex: '0 1 auto',
            '& .MuiTypography-root': {
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }
          }}
        />
      )}
    </ListItemButton>
  );
};

export default NavItem;
